import { FieldConfig, FieldType } from 'components/Forms/types'
import { CreativeKey, CreativeTypeOptions } from './taskTypes'

export const creativeKeyFromProduct = (p: any): CreativeKey | null => {
  const name = (p?.productName || p?.title || '').toLowerCase()
  const cat = (p?.category || p?.category_name || '').toLowerCase()
  const sub = (p?.subcategory || '').toLowerCase()
  if (cat.includes('digital')) return null

  if (name.includes('logo') || sub.includes('logo')) return 'logoDesign'
  if (name.includes('brand')) return 'fullBrandBuild'
  if (cat.includes('website') && name.includes('website') && name.includes('build')) return 'website'
  if (cat.includes('website') || name.includes('website')) return 'websiteDesignOverview'
  if (name.includes('print') || sub.includes('print')) return 'printDesign'
  if (name.includes('photo')) return 'photography'
  if (name.includes('design')) return 'designMaterials'
  if (name.includes('business')) return 'marketingCollateral'
  if (name.includes('video')) return 'video'
  if (cat.includes('social') && (name.includes('ad') || sub.includes('ad'))) return 'socialAds'
  if (name.includes('display')) return 'displayAds'
  if (name.includes('organic')) return 'organicSocial'
  return null
}
export const CREATIVE_FIELDS: Record<keyof CreativeTypeOptions, Record<string, FieldConfig>> = {
  socialAds: {
    social_accessToCreative: {
      label: 'Access to Creative',
      type: FieldType.MULTISELECT,
      options: ['Brand Guide', 'Photography', 'Website Assets'],
      placeholder: 'What type of creative assets do we have access to?',
    },
    socialAdTypes: {
      label: 'Social Ad Types',
      type: FieldType.MULTISELECT,
      options: ['Static (1:1 and 9:16)', 'Static Carousel {Set of 3)', 'Video (1:1 and 9:16'],
    },

    sameOrNewTemplate: {
      label: 'Same or New Template (if applicable)?',
      type: FieldType.SELECT,
      options: ['Same', 'New'],
    },
    prevRequestLink: { label: 'Previous Request Link', type: FieldType.TEXT },
    reasonForRequestChange: { label: 'Reason for Request Change', type: FieldType.TEXTAREA },
    promotingDetails: { label: 'Promoting Details', type: FieldType.TEXTAREA, placeholder: 'What are we promoting?' },
    clientToExclude: {
      label: 'Client to Exclude',
      type: FieldType.TEXT,
      placeholder: 'Has the client specified anything they do not want in the creative?',
    },
    callToAction: { label: 'Call to Action', type: FieldType.TEXTAREA },
    vibe: {
      label: 'Vibe',
      type: FieldType.MULTISELECT,
      options: ['Youthful/Fun', 'Upscale', 'Edgy', 'Minimalistic', 'Modern'],
    },
  },
  displayAds: {
    display_accessToCreative: {
      label: 'Access to Creative',
      type: FieldType.MULTISELECT,
      options: ['Brand Guide', 'Photography', 'Website Assets'],
    },
    displayAdTypes: {
      label: 'Display Ad Types',
      type: FieldType.MULTISELECT,
      options: [
        'Standard Ad Set (160x600, 300x250, 300x600, 300x50, 728x90, 320x50)',
        'Responsive Ad Set (1200x628, 1200x1200)',
      ],
    },
    sameOrNewTemplate: { label: 'Same or New Template?', type: FieldType.SELECT, options: ['Same', 'New'] },
    prevRequestLink: { label: 'Previous Request Link', type: FieldType.TEXT },
    reasonForRequestChange: {
      label: 'Reason for Request Change',
      type: FieldType.TEXTAREA,
      placeholder:
        'Please be specific about the reason for the ad change. Is the leasing season changing? Is it for a new special? Did the client provide new photography? Please provide any and all details you can about this request.',
      required: true,
    },
    promotingDetails: { label: 'Promoting Details', type: FieldType.TEXTAREA, placeholder: 'What are we promoting?' },
    clientToExclude: {
      label: 'Client to Exclude',
      type: FieldType.TEXT,
      placeholder: 'Has the client specified anything they do not want in the creative?',
    },
    callToAction: { label: 'Call to Action', type: FieldType.TEXT },
    vibe: {
      label: 'Vibe',
      type: FieldType.MULTISELECT,
      options: ['Youthful/Fun', 'Upscale', 'Edgy', 'Minimalistic', 'Modern'],
    },
  },
  organicSocial: {
    promotingDetails: {
      label: 'Promoting Details',
      type: FieldType.TEXTAREA,
      placeholder:
        'Are we promoting anything specific? Do they have a new clubhouse? Did rates change? Is availability now limited?',
    },
    callToAction: {
      label: 'Call to Action',
      type: FieldType.TEXTAREA,
      placeholder: 'Should be different from their previous ad set.',
    },
    vibe: {
      label: 'Vibe',
      type: FieldType.MULTISELECT,
      options: ['Youthful/Fun', 'Upscale', 'Edgy', 'Minimalistic', 'Modern'],
    },
  },
  marketingCollateral: {},
  photography: {
    photographyType: {
      label: 'Photography Type',
      type: FieldType.SELECT,
      options: ['Lifestyle', 'Commercial', 'Drone', 'Headshots', 'Other'],
    },
    locations_photography: { label: 'Locations for Photography', type: FieldType.TEXT },
    photoUsage: { label: 'Photo Usage', type: FieldType.SELECT, options: ['Print', 'Web', 'Both'] },
  },
  printDesign: {
    printProjectType: {
      label: 'Print Project Type',
      type: FieldType.SELECT,
      options: ['Brochure', 'Billboard', 'Merchandise', 'Magazine Ad', 'Other'],
    },
    print_dimensions: { label: 'Print Dimensions', type: FieldType.TEXT },
    print_pages: { label: 'Number of Pages', type: FieldType.NUMBER },
    print_creative_asset_link: { label: 'Creative Asset Link', type: FieldType.TEXT },
    stockImages: { label: 'Stock Images', type: FieldType.SELECT, options: ['Yes', 'No', 'Designer’s Choice'] },
    print_phone: { label: 'Phone', type: FieldType.TEXT },
    print_address: { label: 'Address', type: FieldType.TEXT },
    print_website: { label: 'Website', type: FieldType.TEXT },
  },
  designMaterials: {
    printProjectType: {
      label: 'Print Project Type',
      type: FieldType.SELECT,
      options: ['Brochure', 'Billboard', 'Merchandise', 'Magazine Ad', 'Other'],
    },
    print_dimensions: { label: 'Print Dimensions', type: FieldType.TEXT },
    print_pages: { label: 'Number of Pages', type: FieldType.NUMBER },
    print_creative_asset_link: { label: 'Creative Asset Link', type: FieldType.TEXT },
    stockImages: { label: 'Stock Images', type: FieldType.SELECT, options: ['Yes', 'No', 'Designer’s Choice'] },
    print_phone: { label: 'Phone', type: FieldType.TEXT },
    print_address: { label: 'Address', type: FieldType.TEXT },
    print_website: { label: 'Website', type: FieldType.TEXT },
  },
  website: {
    websiteType: {
      label: 'Website Type',
      type: FieldType.SELECT,
      board: 'website',
      options: ['Full Website', 'Landing Page', 'Shopify', 'Other'],
    },
    website_existing_domain: { label: 'Domain (Existing Website)', type: FieldType.TEXT, board: 'website' },
    website_new_domain_input: { label: 'Domain (New Website)', type: FieldType.TEXT, board: 'website' },
    website_new_domain: {
      label: 'Access (New Website Domain)',
      type: FieldType.SELECT,
      board: 'website',
      options: ['Have Access', 'Need Access', 'Need to Purchase', 'Not Sure'],
    },
    website_domain_access: {
      label: 'Access (Existing Website)',
      type: FieldType.SELECT,
      board: 'website',
      options: ['Have Access', 'Need Access', 'Need to Purchase'],
    },
    google_analytics_access: {
      label: 'Access (Google Analytics)',
      type: FieldType.SELECT,
      board: 'website',
      options: ['Have Access', 'Need Access', 'Need to Create'],
    },
    website_content: {
      label: 'Content',
      type: FieldType.SELECT,
      board: 'website',
      options: ['Migrate Content', 'Create new content', 'Migrate & Create'],
    },
    use_stock_images: {
      label: 'Use Stock Images?',
      type: FieldType.SELECT,
      board: 'website',
      options: ['Yes', 'No', 'Defer to creative team'],
    },
    google_map_site: {
      label: 'Need Google map (on site)',
      type: FieldType.SELECT,
      board: 'website',
      options: ['Yes', 'No', 'Defer to creative team'],
    },
    need_contact_form: {
      label: 'Need Contact Form?',
      type: FieldType.SELECT,
      board: 'website',
      options: ['Yes', 'No', 'Defer to creative team'],
    },
    website_brand_style: {
      label: 'Brand Style',
      type: FieldType.MULTISELECT,
      options: [
        'Minimal & Modern',
        'Bold & Geometric',
        'Classic & Elegant',
        'Handwritten/Script',
        'Illustrative & Detailed',
      ],
    },
    website_creative_asset_link: { label: 'Creative Assets Link', type: FieldType.TEXT, board: 'website' },
    badge_compliance_images: { label: 'Badges / Compliance Images Needed', type: FieldType.TEXT },
    // inspiration_links: { label: 'Inspiration Links', type: FieldType.TEXT },
    website_pages: { label: '# of Pages', type: FieldType.NUMBER, placeholder: '# of pages' },
    // main_nav_links: { label: 'Main Nav Links', type: FieldType.TEXTAREA, board: 'website' },
    // main_nav_cta: { label: 'Main Nav CTA', type: FieldType.TEXTAREA, board: 'website' },
    // // footer_nav_links: { label: 'Footer Nav Links', type: FieldType.TEXTAREA, board: 'website' },
    // website_links_info: {
    //   label: 'Links Info',
    //   type: FieldType.TEXTAREA,
    //   board: 'website',
    //   placeholder: 'Inspiration Links | Main Nav Links | Footer Nav Links',
    // },
    // website_addtl_info: { label: 'Addtl Info', type: FieldType.TEXTAREA, board: 'website' },
  },
  websiteDesignOverview: {
    use_stock_images: {
      label: 'Use Stock Images?',
      type: FieldType.SELECT,
      board: 'website',
      options: ['Yes', 'No', 'Defer to creative team'],
    },
    google_map_site: {
      label: 'Need Google map (on site)',
      type: FieldType.SELECT,
      board: 'website',
      options: ['Yes', 'No', 'Defer to creative team'],
    },
    need_contact_form: {
      label: 'Need Contact Form?',
      type: FieldType.SELECT,
      board: 'website',
      options: ['Yes', 'No', 'Defer to creative team'],
    },
    website_brand_style: {
      label: 'Brand Style',
      type: FieldType.MULTISELECT,
      options: [
        'Minimal & Modern',
        'Bold & Geometric',
        'Classic & Elegant',
        'Handwritten/Script',
        'Illustrative & Detailed',
      ],
    },
    website_creative_asset_link: { label: 'Creative Assets Link', type: FieldType.TEXT, board: 'website' },
    badge_compliance_images: { label: 'Badges / Compliance Images Needed', type: FieldType.TEXT },
    inspiration_links: { label: 'Inspiration Links', type: FieldType.TEXT },
  },
  websiteStructure: {
    website_pages: { label: '# of Pages', type: FieldType.NUMBER },
    main_nav_links: { label: 'Main Nav Links', type: FieldType.TEXTAREA, board: 'website' },
    main_nav_cta: { label: 'Main Nav CTA', type: FieldType.TEXTAREA, board: 'website' },
    footer_nav_links: { label: 'Footer Nav Links', type: FieldType.TEXTAREA, board: 'website' },
    website_addtl_info: { label: 'Addtl Info', type: FieldType.TEXTAREA, board: 'website' },
  },
  video: {
    videoType: {
      label: 'Type of Video',
      type: FieldType.SELECT,
      options: ['Lifestyle', 'Interview', 'Advertisement', 'Hype Video', 'Drone'],
    },
    goal_of_video: { label: 'Goal of Video', type: FieldType.TEXT },
    video_target_audience: { label: 'Target Audience', type: FieldType.TEXT },
    video_projected_hours: { label: 'Projected Hours', type: FieldType.NUMBER },
    video_locations: { label: 'Video Locations', type: FieldType.TEXTAREA },
    req_text_logo: { label: 'Required Text or Logos', type: FieldType.TEXT },
    specific_shots: { label: 'Specific Shots Requested', type: FieldType.TEXTAREA },
    output_channels: {
      label: 'Output Channels',
      type: FieldType.MULTISELECT,
      options: ['Social Ads', 'Website', 'Streaming', 'YouTube'],
    },
  },
  fullBrandBuild: {
    deliverables: {
      label: 'Contract Deliverables',
      type: FieldType.MULTISELECT,
      options: ['Logo', 'Visual ID', 'Brand Guide', 'Collateral'],
    },
    target_audience: { label: 'Target Audience', type: FieldType.TEXT },
    use_colors: { label: 'Colors to Use', type: FieldType.TEXT },
    avoid_colors: { label: 'Colors to Avoid', type: FieldType.TEXT },
    logo_inspiration: { label: 'Logo Inspiration', type: FieldType.TEXT },
    brand_style: {
      label: 'Brand Style',
      type: FieldType.MULTISELECT,
      options: [
        'Minimal & Modern',
        'Bold & Geometric',
        'Classic & Elegant',
        'Handwritten/Script',
        'Illustrative & Detailed',
      ],
    },
    primary_color: { label: 'Primary Color', type: FieldType.COLOR_PICKER },
    secondary_color: { label: 'Secondary Color', type: FieldType.COLOR_PICKER },
    accent_color: { label: 'Accent Color', type: FieldType.COLOR_PICKER },
  },
  logoDesign: {
    deliverables: {
      label: 'Contract Deliverables',
      type: FieldType.MULTISELECT,
      placeholder: 'Logo, Visual Id, Brand Guide ...',
      options: ['Logo', 'Visual ID', 'Brand Guide'],
    },
    target_audience: { label: 'Target Audience', type: FieldType.TEXT },
    use_colors: { label: 'Colors to Use', type: FieldType.TEXT },
    avoid_colors: { label: 'Colors to Avoid', type: FieldType.TEXT },
    logo_inspiration: { label: 'Logo Inspiration', type: FieldType.TEXT },

    logo_brand_style: {
      label: 'Brand Guide',
      type: FieldType.MULTISELECT,
      options: [
        'Minimal & Modern',
        'Bold & Geometric',
        'Classic & Elegant',
        'Handwritten/Script',
        'Illustrative & Detailed',
      ],
    },
    primary_color: { label: 'Primary Color', type: FieldType.COLOR_PICKER },
    secondary_color: { label: 'Secondary Color', type: FieldType.COLOR_PICKER },
    accent_color: { label: 'Accent Color', type: FieldType.COLOR_PICKER },
  },
}
